<script setup>
import StoreFrontDefaultLayout from "@/Layouts/StoreFrontDefaultLayout.vue"
import PartnerStoreFrontDefaultLayout from "@/Layouts/PartnerStoreFrontDefaultLayout.vue"
import {Head, router, useForm, usePage} from "@inertiajs/vue3"
import CBreadcrumb from "@/Components/CBreadcrumb.vue"
import {computed, h, nextTick, onMounted, ref, watch} from 'vue'
import {helpers} from "@/Services/helpers.js";
import CProductDetailColorVariationsBullets from "@/Components/CProductDetailColorVariationsBullets.vue";
import {eventBus} from "@/Services/eventBus.js";
import CRelatedProducts from "@/Components/CRelatedProducts.vue";
import PlateForm from "@/Pages/ProductsTemplates/PlateForm.vue";
import CTooltip from "@/Pages/Admin/Partners/Portals/CTooltip.vue";

const props = defineProps({
    breadcrumbs: Array,
    productDetail: Object,
})

const page = usePage()
const options = portalOptions

const descriptionExpanded = ref(false)
const descriptionSheetRef = ref(false)
const descriptionHeight = ref(0)
const clickedStockSupplier = ref({})
const clickedStockCreative = ref({})
const openReportDialog = ref(false)
const openMaxImage = ref(false)

const productDetail = props.productDetail

const product = productDetail.product
const variations = product.variations
const sel = ref(0)

const selectedImageUrl = computed(() => {
    const availableMedias = selectedVariation.value
        ? selectedVariation.value.item_profile.medias || [{url: selectedVariation.value.media_url}]
        : props.productDetail.medias

    if (availableMedias && availableMedias[sel.value]?.url) {
        return availableMedias[sel.value].url
    }

    if (product.media_url) {
        return product.media_url
    }

    if (product.variations && product.variations.length > 0) {
        for (const variation of product.variations) {
            if (variation.media_url) {
                return variation.media_url
            }
        }
    }

    return '/assets/img/no-photo.png'
})

const filteredMedias = computed(() => {
    if (selectedVariation.value) {
        return selectedVariation.value.item_profile.medias || [{url: selectedVariation.value.media_url}]
    }
    return props.productDetail.medias || []
})

const showArrows = computed(() => {
    return filteredMedias.value.length > 3
})

const showMessage = ref(false)
const variantsQuantity = ref([])
const submitCustomizableTemplateForm = ref(false)
const plateFormRef = ref(null)

const selectedQuantities = ref(Array(variations.length).fill().map((_, index) => options.productMinAmount ? variations[index].item_profile.quant_min : 1))

const hasColor = (color) => {
    return !!color.hex;
}
const next = () => {
    if (sel.value < filteredMedias.value.length - 1) sel.value++
}

const prev = () => {
    if (sel.value > 0) sel.value--
}

const productReference = function () {
    return props.productDetail.selectedAttributes ? variations[0].reference : product.reference
}

const updateDescriptionSheetHeight = function () {
    nextTick(() => {
        descriptionHeight.value = descriptionSheetRef.value.$el.scrollHeight
    })
}

const getCurrentStock = (product, provider) => {
    if (product !== null) {
        const productRef = product.reference

        if (provider === 'creative') {
            return clickedStockCreative.value[productRef] || productVariationDefault(product).stock.local
        }

        return clickedStockSupplier.value[productRef] || productVariationDefault(product).stock.fornecedor
    }
}

const productVariationDefault = function (product) {
    if (product.variations && product.variations.length === 1) {
        return product.variations[0]
    }

    return product
}

const selectedVariation = computed(() => {
    return productDetail.selectedAttributes ? variations[0] : null
})

const selectedVariationIndex = computed(() => {
    if (selectedVariation.value) {
        return variations.findIndex(v => v.id === selectedVariation.value.id)
    }
    return 0
})

const productTitle = () => {
    let title = product.item_profile.product_title;

    if (selectedVariation.value !== null) {
        title += ' - ' + helpers.firstLetterUppercase(selectedVariation.value.color)
    }

    return title;
}

const productSpecs = computed(() => {
    return [
        {
            label: '<strong>Largura:</strong>',
            value: `${product.item_profile.width} mm`,
            visible: product.item_profile.width > 0
        },
        {
            label: '<strong>Altura:</strong>',
            value: `${product.item_profile.height} mm`,
            visible: product.item_profile.height > 0
        },
        {
            label: '<strong>Profundidade</strong>',
            value: `${product.item_profile.length} mm`,
            visible: product.item_profile.length > 0
        },
        {
            label: '<strong>Peso:</strong>',
            value: `${product.item_profile.weight} kg`,
            visible: product.item_profile.weight > 0
        },
        {
            label: '<strong>Tabela de tamanhos:</strong>',
            value: `<c-link href='#img-size-grid' class="smooth-scroll cursor-pointer" color="primary">Clique aqui</c-link>`,
            visible: product.item_profile.size_grid_img
        }
    ].filter(spec => spec.visible)
})

const additionalSpecs = computed(() => {
    return [
        {
            label: '<strong>Creative:</strong>',
            value: `${getCurrentStock(selectedVariation.value ?? product, 'creative')}`,
            visible: productDetail.canViewStock
        },
        {
            label: '<strong>Fornecedor:</strong>',
            value: `${getCurrentStock(selectedVariation.value ?? product, 'fornecedor')}`,
            visible: productDetail.canViewStock
        },

    ].filter(spec => spec.visible)
})

const canAddToCart = computed(() => {
    // exibimos o botão de adicionar ao carrinho se apenas uma variação possivel
    // ou se não tiver opção de escolha de cor ou se uma cor esta selecionada
    return variations.length === 1 || product.colors.length <= 1 || route().params.color
})

const getVariantsQuantity = async function () {
    variantsQuantity.value = product.variations.map((variation, index) => ({
            id: variation.id,
            quantity: selectedQuantities.value[index] || 0
        })
    )

    if (plateFormRef.value) {
        submitCustomizableTemplateForm.value = true
    }

    return variantsQuantity.value
}

const resetVariantsQuantity = function () {
    product.variations.map((variation, index) => {
        selectedQuantities.value[index] = variation.item_profile.quant_min
    })
}

const addItemCart = async function () {
    const items = await getVariantsQuantity()

    router.post(route('cart.add'), {items}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
            eventBus.emit('openCart')
            helpers.toast('O produto foi adicionado ao carrinho com sucesso!')
            resetVariantsQuantity()
        },
        onError: (errors) => {
            if (plateFormRef.value && errors['items.0.metadata.customizableImage']) {
            }
        }
    })
}

const productReportForm = useForm({
    comment: null,
    productVariationId: null
})

const handleFormSubmit = async function () {
    try {
        if (productDetail.selectedAttributes?.length > 0) {
            productReportForm.productVariationId = variations[0].id
        }

        productReportForm.post(route('product.process-review', {product: product.id}), {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                helpers.toast('Revisão enviada com sucesso!')
                openReportDialog.value = false
                productReportForm.reset()
            }
        })
    } catch (error) {
        console.error('Erro ao processar formulário:', error)
        helpers.toast('Ocorreu um erro inesperado', 'error')
    }
}

onMounted(() => {
    document.querySelectorAll('.smooth-scroll').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault()

            const targetId = this.getAttribute('href').substring(1)
            const targetElement = document.getElementById(targetId)

            if (targetElement) {
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })

                history.pushState(null, null, `#${targetId}`)
            }
        })
    })
})

watch(descriptionSheetRef, () => {
    updateDescriptionSheetHeight()
})

watch(() => productDetail?.selectedAttributes ?? [], () => {
    selectedVariation.value = productDetail?.selectedAttributes?.length > 0 ? variations[0] : null
})

watch(selectedVariation, () => {
    sel.value = 0
})

defineOptions({
    layout: portalOptions?.mainPortal
        ? h(StoreFrontDefaultLayout, {cart: true, bgColor: 'white', switchCategoryMenu: portalOptions.switchCategoryMenu})
        : h(PartnerStoreFrontDefaultLayout, {cart: true, bgColor: 'white', switchCategoryMenu: portalOptions.switchCategoryMenu})
})

</script>

<template>
    <Head :title="'Detalhes do Produto: ' + productTitle()"></Head>
    <v-sheet class="w-75 mx-auto">
        <c-breadcrumb :items="breadcrumbs"/>
        <v-row>
            <v-col cols="12" class="pa-4">
                <v-row class="d-flex align-center">
                    <v-col cols="10" class="justify-end">
                        <h1>{{ productTitle() }} </h1>
                        <p class="text-subtitle-2"> Código do produto: {{ productReference() }}</p>
                    </v-col>
                    <v-col cols="2" class="ms-auto">
                        <v-btn v-if="page.props.auth.permissions.includes('update portal product variations')" rounded size="36" class="float-end mt-1"
                               @click="router.get(route('admin.portal.products', {portal: productDetail.portalId, search: product.reference }))">
                            <v-icon icon="mdi-pencil" size="20" color="grey-darken-2"/>
                        </v-btn>
                        <v-btn v-if="page.props.auth.permissions.includes('report product error')" rounded size="36"
                               @click="openReportDialog = true" class="float-end me-2 mt-1">
                            <v-icon icon="mdi-alert-circle" size="20" color="grey-darken-2"/>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" sm="12" md="6" order="2" order-md="1">
                <v-row class="">
                    <v-col cols="12">
                        <v-img
                            :src="selectedImageUrl"
                            class="mx-auto pa-0 ma-0"
                            width="80%"
                            @click="openMaxImage = true"
                        />
                    </v-col>
                    <v-divider/>
                    <v-col cols="12" class="d-flex justify-center">
                        <div v-if="showArrows" class="d-flex align-center">
                            <div @click="prev" style="cursor: pointer">
                                <v-icon icon="mdi-chevron-left" size="2rem"/>
                            </div>
                        </div>
                        <v-slide-group
                            v-model="sel"
                            class="py-2"
                            selected-class="bg-primary"
                            :show-arrows="showArrows"
                        >
                            <v-slide-group-item
                                v-for="(media, index) in filteredMedias" :key="media.url"
                                v-slot="{ isSelected }"
                            >
                                <v-img
                                    width="120"
                                    :src="media.url"
                                    cover
                                    eager
                                    class="me-4"
                                    :class="isSelected ? 'border border-grey-lighten-2 rounded-lg' : ''"
                                    style="cursor: pointer"
                                    @click="sel = index"
                                />
                            </v-slide-group-item>
                        </v-slide-group>
                        <div v-if="showArrows" class="d-flex align-center">
                            <div @click="next" style="cursor: pointer">
                                <v-icon icon="mdi-chevron-right" size="2rem"/>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-col>

            <v-col cols="12" sm="12" md="6" order="3" order-md="2">
                <v-row>
                    <v-col cols="12" v-if="props.productDetail.canViewPrice">
                        <p class="pa-0 ma-0"><strong>Preço unitário:</strong>
                            <c-tooltip message="Não incluso preço de gravação"></c-tooltip>
                        </p>
                        <h2 v-if="product.item_profile.max_price !== undefined && product.item_profile.min_price !== undefined">
                            R$ {{ helpers.formatPrice(product.item_profile.min_price) }} -
                            {{ helpers.formatPrice(product.item_profile.max_price) }}
                        </h2>
                        <h2 v-else>
                            R$ {{ helpers.formatPrice(product.item_profile.max_price) }}
                        </h2>
                    </v-col>
                    <v-col cols="12">
                        <p class="pb-1"><strong>Cores Disponíves:</strong></p>
                        <c-product-detail-color-variations-bullets
                            :active-color="selectedVariation"
                            :product="product">
                        </c-product-detail-color-variations-bullets>
                    </v-col>
                    <v-col cols="12">
                        <p class="pb-1"><strong>Descrição:</strong></p>
                        <v-sheet class="overflow-hidden text-justify faded-end" v-bind:class="{'expanded': descriptionExpanded}"
                                 @click="descriptionExpanded = true" ref="descriptionSheetRef" :style="{
                            height: descriptionExpanded ? descriptionHeight + 'px' : '5rem',
                            transition: 'height 0.3s ease',
                            whiteSpace: 'pre-line'
                        }">
                            {{ product.item_profile.description }}
                        </v-sheet>
                        <v-sheet class="text-center" style="height: 30px" v-if="descriptionHeight > 50">
                            <v-btn :icon="descriptionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="descriptionExpanded = !descriptionExpanded"
                                   variant="flat" :color="descriptionExpanded ? 'grey-lighten-2' : 'transparent'" size="30"
                                   :style="{'margin-top': descriptionExpanded ? '0' : '-10px'}"/>
                        </v-sheet>
                    </v-col>
                    <v-col cols="12">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <p class="pb-1"><strong>Caracteristicas:</strong></p>
                                <v-table class="text-caption" density="compact">
                                    <tbody>
                                    <tr v-for="(spec, index) in productSpecs" :key="index">
                                        <th v-html="spec.label"></th>
                                        <td v-html="spec.value"></td>
                                    </tr>
                                    </tbody>
                                </v-table>
                            </v-col>
                            <v-col cols="12" sm="6" v-if="product.colors.length <= 1 || (selectedVariation && !selectedVariation?.size)">
                                <p class="pb-1"><strong>Estoque:</strong></p>
                                <v-table class="text-caption" density="compact">
                                    <tbody>
                                    <tr v-for="(spec, index) in additionalSpecs" :key="index">
                                        <th v-html="spec.label"></th>
                                        <td v-html="spec.value"></td>
                                    </tr>
                                    </tbody>
                                </v-table>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" class="ma-0 pa-0 my-5 ps-4">
                        <v-sheet v-if="selectedVariation && selectedVariation?.size">
                            <p class="pb-1"><strong>Tamanhos:</strong></p>
                            <v-row v-for="(variation, index ) in variations" :key="index" class="ma-0">
                                <v-col cols="12">
                                    <v-row class="d-flex align-center">
                                        <v-col cols="12" sm="3" md="2" class="text-left">
                                            <h2>{{ variation.size }} - </h2>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="6" class="align-center">
                                            <div v-if="productDetail.canViewStock">
                                                <p><strong>Estoque Creative: </strong> {{ getCurrentStock(variation, 'creative') }}</p>
                                            </div>
                                            <div v-if="productDetail.canViewStock">
                                                <p><strong>Estoque Fornecedor: </strong>{{ getCurrentStock(variation, 'fornecedor') }}</p>
                                            </div>
                                        </v-col>
                                        <v-col cols="12" sm="3" md="4" class="justify-end align-center">
                                            <v-row>
                                                <v-col cols="12" class="d-flex justify-end">
                                                    <v-number-input
                                                        v-model="selectedQuantities[index]"
                                                        :min="1"
                                                        :max="999999"
                                                        :step="10"
                                                        variant="underlined"
                                                        density="compact"
                                                        control-variant="stacked"
                                                        class="number-picker custom-controls mx-3"
                                                        max-width="200px"
                                                        inset
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-sheet>
                        <v-sheet v-else-if="!hasColor(product.colors[0]) || selectedVariation && !selectedVariation?.size">
                            <v-row>
                                <v-col cols="12" class="pt-5" height="20vh" width="40vw"
                                       v-if="(selectedVariation?.item_profile?.customizable === 1) ||
                                (variations[0]?.item_profile.customizable === 1) &&
                                page.props.auth.user">
                                    <p class="pb-1"><strong>Informaçoes Adicionais:</strong></p>
                                    <plate-form ref="plateFormRef"
                                                v-if="selectedVariation?.item_profile?.customizable_template === 'plate' || variations[0]?.item_profile?.customizable_template === 'plate'"
                                                :variant="selectedVariation"
                                                v-model:variants="variantsQuantity" v-model:submitForm="submitCustomizableTemplateForm"/>
                                </v-col>
                            </v-row>
                        </v-sheet>
                        <v-col cols="12" v-if="product.colors.length <= 1 || (selectedVariation && !selectedVariation?.size)">
                            <v-row>
                                <v-col cols="12" class="d-flex justify-end pa-0">
                                    <v-number-input
                                        v-model="selectedQuantities[selectedVariationIndex]"
                                        :min="1"
                                        :max="999999"
                                        :step="10"
                                        variant="underlined"
                                        density="compact"
                                        control-variant="stacked"
                                        class="number-picker custom-controls mx-3"
                                        max-width="200px"
                                        inset
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="12" class="d-flex justify-end mt-5 pa-0">
                            <v-btn @click="canAddToCart ? addItemCart() : showMessage = true" variant="flat" color="primary" class="mobile-full-width-btn">
                                <span class="font-weight-bold">ADICIONAR AO ORÇAMENTO</span>
                            </v-btn>
                        </v-col>
                        <v-col cols="12" class="d-flex justify-end pa-0 mt-2">
                            <span v-if="showMessage" class="text-center text-red subtitles-2">
                                    Selecione uma variação para adicionar ao carrinho.
                            </span>
                        </v-col>
                    </v-col>
                </v-row>
            </v-col>

            <v-col cols="12" v-show="product.item_profile.size_grid_img" class="mt-10" order="4">
                <v-img
                    :src="product.item_profile.size_grid_img"
                    eager
                    class="mx-auto"
                    width="90%"
                    id="img-size-grid"
                />
            </v-col>
        </v-row>
        <v-row class="py-10">
            <v-col md="12">
                <h2><span class="text-grey-darken-4 font-weight-bold">Produtos Relacionados</span></h2>
            </v-col>
            <v-col md="12">
                <c-related-products :products="productDetail.relatedProducts"/>
            </v-col>
        </v-row>
    </v-sheet>

    <v-dialog v-model="openReportDialog" max-width="500px">
        <v-card>
            <v-card-title class="pa-6 py-4 font-weight-bold">Reportar Erro no Produto</v-card-title>

            <v-card-text class="pa-6 py-4">
                <v-form @submit.prevent="handleFormSubmit">
                    <v-textarea
                        v-model="productReportForm.comment"
                        label="Descreva o problema encontrado"
                        :error-messages="productReportForm.errors.comment"
                    ></v-textarea>
                </v-form>
            </v-card-text>

            <v-card-actions class="pa-6 py-4">
                <v-spacer></v-spacer>
                <v-btn color="secondary" variant="flat" @click="openReportDialog = false">Cancelar</v-btn>
                <v-btn color="primary" variant="flat" type="submit" @click="handleFormSubmit" :loading="productReportForm.processing"
                >Enviar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="openMaxImage">
        <v-card>
            <v-card-actions class="justify-end">
                <v-btn @click="openMaxImage = false" variant="text">Voltar</v-btn>
            </v-card-actions>
            <v-card-item>
                <v-img
                    :src="selectedImageUrl"
                    class="mx-auto"
                    max-height="80vh"
                    contain
                />
            </v-card-item>

        </v-card>
    </v-dialog>

</template>

<style scoped>
.v-slide-group :deep(.v-slide-group__prev),
.v-slide-group :deep(.v-slide-group__next) {
    display: none !important;
}

.faded-end {
    position: relative;
}

.faded-end::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: linear-gradient(to bottom, transparent, white);
    transition: opacity 0.3s ease;
}

.faded-end.expanded::after {
    display: none;
}

.v-table th, .v-table td {
    min-width: 80px;
    width: 50%;
}

/* Responsive table adjustments */
@media (max-width: 768px) {
    .v-table th, .v-table td {
        min-width: 60px;
        width: auto;
        font-size: 0.75rem;
        padding: 8px 4px;
    }

    .v-table {
        font-size: 0.7rem;
    }
}

@media (max-width: 480px) {
    .v-table th, .v-table td {
        min-width: 40px;
        width: auto;
        font-size: 0.7rem;
        padding: 6px 2px;
    }
}

.v-table td:empty,
.v-table th:empty,
.v-table td:only-child:has(br:only-child),
.v-table th:only-child:has(br:only-child) {
    visibility: visible;
    height: 40px;
    border: 0 !important;
}

.v-table td:has(span:empty),
.v-table th:has(span:empty),
.v-table td:only-child:has(span:only-child:empty),
.v-table th:only-child:has(span:only-child:empty) {
    border: 0 !important;
}

.custom-controls :deep(.v-number-input__control:last-child::after) {
    content: "+";
    font-weight: bold;
    color: #272727;
    position: absolute;
    height: 100%;
    z-index: -10;
}

.custom-controls :deep(.v-btn__overlay) {
    background-color: transparent !important;
}

.custom-controls :deep(.v-number-input__control:last-child::before) {
    content: "−";
    font-weight: bold;
    color: #272727;
    position: absolute;
    top: 47%;
    z-index: -10;
}

.custom-controls :deep(.v-number-input__control) {
    height: 3rem;
    width: 15px;
    position: relative;
    align-items: center;
}

.custom-controls :deep(.v-number-input__control i) {
    display: none;
}

.custom-controls :deep(.v-number-input__control .v-divider) {
    width: 15px;
    align-self: auto;
}

.number-picker {
    background-color: white !important;
    border: none !important;
    border-radius: 0 !important;
    align-items: center !important;
}

.number-picker :deep(.v-field__input) {
    padding-top: 0.5rem !important;
}

.number-picker :deep(.v-field__field) {
    align-items: center !important;
    padding-left: 0.3rem;
}

.number-picker :deep(.v-field__outline):after {
    border-width: 0 0 var(--v-field-border-width);
}

@keyframes blink {
    from {
        opacity: 0.7;
    }
    to {
        opacity: 1;
    }
}

html, body {
    scroll-behavior: smooth;
}

.v-slide-group-item .v-img {
    width: 120px !important;
    height: 120px !important;
    aspect-ratio: 1 / 1;
    object-fit: cover;
}

@media (max-width: 1440px) {
    .v-sheet.w-75 {
        width: 85% !important;
    }
}

@media (max-width: 780px) and (min-width: 769px) {
    .v-sheet.w-75 {
        width: 95% !important;
    }

    .v-slide-group-item .v-img {
        width: 140px !important;
        height: 140px !important;
    }

    .size-variation-row .v-col {
        flex-direction: column;
        align-items: flex-start !important;
    }
}

@media (max-width: 768px) {
    .v-sheet.w-75 {
        width: 100% !important;
        padding: 0 16px;
    }

    .v-slide-group-item .v-img {
        width: 100px !important;
        height: 100px !important;
    }

    .mobile-full-width-btn {
        width: auto !important;
        min-width: 200px !important;
        margin: 0 16px !important;
    }

    .v-sheet.w-75 {
        margin: 0 auto 16px auto !important;
    }

    h1 {
        font-size: 1.5rem !important;
        line-height: 1.3 !important;
    }

    h2 {
        font-size: 1.25rem !important;
    }
}
</style>
